import React, { useEffect, useRef } from 'react';

interface RouteMapPreviewProps {
  coordinates: number[][];
  height?: string;
}

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

let leafletLoaded = false;
let leafletLoadPromise: Promise<void> | null = null;

const loadLeaflet = (): Promise<void> => {
  if (leafletLoaded) return Promise.resolve();
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve) => {
    // Load CSS
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    // Load JS
    if ((window as any).L) {
      leafletLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.onload = () => {
      leafletLoaded = true;
      resolve();
    };
    document.head.appendChild(script);
  });

  return leafletLoadPromise;
};

const RouteMapPreview: React.FC<RouteMapPreviewProps> = ({ coordinates, height = '400px' }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (!coordinates || coordinates.length === 0) return;

    let cancelled = false;

    loadLeaflet().then(() => {
      if (cancelled || !mapRef.current) return;

      const L = (window as any).L;

      // Clean up existing map
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      const map = L.map(mapRef.current);
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      // Coordinates from backend are [longitude, latitude, elevation?]
      const latLngs = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

      const polyline = L.polyline(latLngs, {
        color: '#C25D2E',
        weight: 4,
        opacity: 0.8,
      }).addTo(map);

      // Add start marker
      if (latLngs.length > 0) {
        L.circleMarker(latLngs[0], {
          radius: 8,
          color: '#3E5E53',
          fillColor: '#3E5E53',
          fillOpacity: 1,
        }).addTo(map).bindPopup('Start');

        // Add end marker
        L.circleMarker(latLngs[latLngs.length - 1], {
          radius: 8,
          color: '#C25D2E',
          fillColor: '#C25D2E',
          fillOpacity: 1,
        }).addTo(map).bindPopup('End');
      }

      map.fitBounds(polyline.getBounds(), { padding: [30, 30] });
    });

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [coordinates]);

  if (!coordinates || coordinates.length === 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
          color: '#666',
        }}
      >
        No route path available
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      style={{
        height,
        width: '100%',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
      }}
    />
  );
};

export default RouteMapPreview;
